import * as RadixDialog from '@radix-ui/react-dialog';
import { Button } from '@scholastic/mockingjay';
import { useEffect, useState } from 'react';
import { useNavigate, useRouteLoaderData } from 'react-router-dom';
import {
  GOOGLE_IMPORT_SOURCES,
  useGoogleImportReturn,
} from 'eds-roster-ux/import-google-classes-content';
import { useGetMeApi } from 'eds-common/api/v2';
import Close from '@/assets/icons/close.svg';
import { useWelcomeGuide } from '@/hooks/use-welcome-guide/use-welcome-guide';
import welcomeModalHeader from '@/assets/images/welcome-modal-header.png';
import { ImportGoogleClassesDialog } from '@/components/ImportGoogleClassesDialog/ImportGoogleClassesDialog';
import { AccessCodeRedemptionDialog } from '@/components/AccessCodeRedemptionDialog/AccessCodeRedemptionDialog';
import type { HomePageLoaderData } from '@/pages/HomePage/HomePage';

export function WelcomeModal() {
  const homeLoaderData = useRouteLoaderData('home') as
    | HomePageLoaderData
    | undefined;
  const navigate = useNavigate();
  const { step, isSessionPending, markAsSeen, markAsArchived } =
    useWelcomeGuide();
  const { data: user } = useGetMeApi();
  const isEducator =
    user?.currentRole === 'teacher' || user?.currentRole === 'administrator';
  const isGoogleUser = user?.userSource === 'GoogleClassroom';
  const isMyScholasticUser = user?.userSource === 'MyScholastic' && isEducator;
  const isMrsUser =
    user?.userSource === 'Ednition' &&
    user?.otherSource?.toLowerCase() === 'mrs' &&
    isEducator;
  const { shouldAutoOpen } = useGoogleImportReturn(
    GOOGLE_IMPORT_SOURCES.dialog
  );
  const isAccessCodeDeepLinkArrival =
    Boolean(homeLoaderData?.accessCodeRedemptionInitialState) ||
    Boolean(homeLoaderData?.appDeepLinkNoAccess) ||
    Boolean(homeLoaderData?.openAccessCodeRedemption);
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(!shouldAutoOpen);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(shouldAutoOpen);
  const [isAddProductDialogOpen, setIsAddProductDialogOpen] = useState(false);

  const closeWelcomeModal = () => {
    setIsWelcomeModalOpen(false);
    markAsArchived();
  };

  useEffect(() => {
    if (isAccessCodeDeepLinkArrival || !step || isSessionPending) {
      return;
    }

    if (!shouldAutoOpen) {
      markAsSeen();
      return;
    }

    markAsArchived();
  }, [
    isAccessCodeDeepLinkArrival,
    shouldAutoOpen,
    step,
    isSessionPending,
    markAsSeen,
    markAsArchived,
  ]);

  if (isAccessCodeDeepLinkArrival) {
    return null;
  }

  if (!step && !isImportDialogOpen && !isAddProductDialogOpen) {
    return null;
  }

  const stepContent = (step?.content ?? {
    body: '',
    title: '',
  }) as {
    title: string;
    body: string;
  };

  const handleWelcomeModalOpenChange = (value: boolean) => {
    if (value) {
      setIsWelcomeModalOpen(true);
      return;
    }

    closeWelcomeModal();
  };

  const handleOpenImportDialog = () => {
    closeWelcomeModal();
    setIsImportDialogOpen(true);
  };

  const handleOpenAddProductDialog = () => {
    closeWelcomeModal();
    setIsAddProductDialogOpen(true);
  };

  const handleSetUpClasses = () => {
    closeWelcomeModal();
    navigate('/classes');
  };

  const renderActions = () => {
    if (isGoogleUser) {
      return (
        <>
          <RadixDialog.Close asChild>
            <Button variant="secondary">Set up later</Button>
          </RadixDialog.Close>
          <Button variant="primary" onClick={handleOpenImportDialog}>
            Import classes
          </Button>
        </>
      );
    }

    if (isMrsUser) {
      return (
        <>
          <RadixDialog.Close asChild>
            <Button variant="secondary">Do it later</Button>
          </RadixDialog.Close>
          <Button variant="primary" onClick={handleSetUpClasses}>
            Manage classes
          </Button>
        </>
      );
    }

    if (isMyScholasticUser) {
      return (
        <>
          <RadixDialog.Close asChild>
            <Button variant="secondary">Set up later</Button>
          </RadixDialog.Close>
          <Button variant="primary" onClick={handleOpenAddProductDialog}>
            Add product
          </Button>
        </>
      );
    }

    return (
      <RadixDialog.Close asChild>
        <Button variant="primary">Close</Button>
      </RadixDialog.Close>
    );
  };

  return (
    <>
      {step ? (
        <RadixDialog.Root
          open={isWelcomeModalOpen}
          onOpenChange={handleWelcomeModalOpenChange}
        >
          <RadixDialog.Portal>
            <RadixDialog.Overlay
              className="fixed inset-0 grid place-items-center overflow-y-auto bg-black/75 p-6"
              data-testid="welcome-modal-overlay"
            >
              <RadixDialog.Content
                className="relative max-w-[700px] bg-brand-white"
                onEscapeKeyDown={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
              >
                <img
                  src={welcomeModalHeader}
                  alt=""
                  className="h-[161px] w-full object-cover object-right-top"
                />
                <div className="p-8">
                  <RadixDialog.Title
                    className="mb-6 text-3xl font-bold text-red-700"
                    asChild
                  >
                    <div
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: stepContent.title }}
                    />
                  </RadixDialog.Title>
                  <RadixDialog.Description asChild>
                    <div
                      className="text-neutral-900"
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: stepContent.body }}
                    />
                  </RadixDialog.Description>
                  <div className="mt-14 flex justify-end gap-4">
                    {renderActions()}
                  </div>
                </div>
                <RadixDialog.Close asChild>
                  <Button
                    variant="tertiary"
                    className="absolute right-4 top-4 min-w-fit p-1"
                    aria-label="Close"
                    data-testid="welcome-modal-close-button"
                  >
                    <Close aria-hidden="true" className="size-4" />
                  </Button>
                </RadixDialog.Close>
              </RadixDialog.Content>
            </RadixDialog.Overlay>
          </RadixDialog.Portal>
        </RadixDialog.Root>
      ) : null}

      <ImportGoogleClassesDialog
        key={String(isImportDialogOpen)}
        defaultOpen={isImportDialogOpen}
        onClose={() => setIsImportDialogOpen(false)}
        userName={user?.givenName ?? ''}
      />

      {isAddProductDialogOpen && (
        <AccessCodeRedemptionDialog
          open={isAddProductDialogOpen}
          onOpenChange={setIsAddProductDialogOpen}
        />
      )}
    </>
  );
}
